<template>
  <div>
    <chart
      v-if="showChart"
      :chartOptions="finalChartOptions" :monthWindow="monthsWindow" :dynamicXAxis="dynamicXAxis" :xAxisDayInterval="7"
      :applyGradient="true" :chartBackgroundColor="chartBackgroundColor"
      :chartTitle="titleChart" :titleOptions="{ align: 'center', style: { color: titleChartColor } }"
      :legendOptions="{ enabled: false }"
      :markersEnabled="true" :markerRadius="4" />
      <p v-else class="text-center text-muted">
     {{ $t('not-enough-data-chart') }}
   </p>
  </div>
</template>

<script>
import Chart from '@/components/base/Chart.vue'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'

import {
  BLUE_COLOUR, RED_COLOUR, GRAY_COLOUR, ORANGE_MID_COLOUR,
  GREEN_LIGHT_COLOUR, BACKGROUND_COLOUR, YELLOW_COLOUR, GREEN_COLOUR
} from '@/constants/colours'
import { BLOOD_PRESSURE, HEALTH_CONDITION_TODAY } from '@/constants/clinicHistory'
import clinicHistoryVariableColorsMixin from '@/mixins/clinicHistoryVariableColors.mixin'
import i18nMixin from '@/mixins/i18n.mixin'
import {renderTickIconHtml, getMoodEmojiKey} from '@/helpers/icon.helpers'

const I18N_FILE = 'clinic-history'
const MONTH_MS = 30.44 * 24 * 60 * 60 * 1000
const SIX_MONTHS_MS = 6 * MONTH_MS

export default {
  name: 'ClinicHistoryVariableChart',
  mixins: [clinicHistoryVariableColorsMixin, i18nMixin(I18N_FILE)],
  components: {
    Chart
  },
  props: {
    variableName: {
      type: String,
      required: true
    },
    coloringPoints: {
      type: Boolean,
      default: false
    },
    dynamicXAxis: {
      type: Boolean,
      default: false
    },
    oneValuePerDay: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default () {
        return this.$t(`${this.variableName}`)
      }
    },
    maxValue: {
      type: Number,
      required: false
    },
    showIntervalArrows: {
      type: Boolean,
      default: true
    },
    chartDatas: {
      type: Object,
      default: () => ({
        variableData: []
        // moodsData: []
      })
    },
    // yAxisAdditional: {
    //   type: Array,
    //   default () {
    //     return [
    //       {
    //         labels: { style: { color: 'black' } },
    //         min: 0,
    //         gridLineColor: GRAY_COLOUR,
    //         gridLineWidth: 0.25,
    //         opposite: true
    //       }
    //     ]
    //   }
    // },
    /**
     * Series adicionales que se añadirán al gráfico.
     * Deben ser objetos Highcharts válidos y pueden indicar su propio yAxis
     */
    additionalSeries: {
      type: Array,
      default: () => []
    },
    titleChart: {
      type: String,
      default () {
        return this.$t(`${this.variableName}`)
      }
    },
    minWidthChart: {
      type: Number,
      default: 1000
    },
    typeChart: {
      type: String,
      default () {
        return 'spline'
      }
    },
    hideMainSeriesByDefault: {
      type: Boolean,
      default: false
    },

    /**  Oculta las series adicionales al iniciar */
    hideAdditionalSeriesByDefault: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      chartData: JSON.parse(JSON.stringify(this.chartDatas)),
      titleChartColor: BLUE_COLOUR,
      chartBackgroundColor: BACKGROUND_COLOUR,
      showChart: false,
      monthsWindow: 6
    }
  },
  computed: {
    // scrollableOpts () {
    //   const totalPoints = (this.chartData.variableData || []).length
    //   const SIX_MONTH_POINTS = 24
    //   if (totalPoints <= SIX_MONTH_POINTS) return null
    //   // densidad: 60 px por punto ≈ 1440 px para 24 pts.
    //   const pxPerPoint = 40
    //   const minWidth = Math.max(this.minWidthChart, totalPoints * pxPerPoint)
    //   return { minWidth, scrollPositionX: 1 }
    //   },
    // ----------------------------------------------------------------------
    // Indica si esta variable maneja min/max (ej. presión arterial)
    // ----------------------------------------------------------------------
    minMaxTypeChart () {
      return this.variableName === BLOOD_PRESSURE
    },
    autoScale () {
      return typeof this.maxValue !== 'number'
    },

    // ----------------------------------------------------------------------
    // Indica la unidad de medida, si está definida en i18n
    // ----------------------------------------------------------------------
    measureUnit () {
      const unitMsgKey = `measureUnits.${this.variableName}`
      const translated = this.$t(unitMsgKey)
      return !translated.includes('measureUnits.') ? ` ${translated}` : ''
    },

    // ----------------------------------------------------------------------
    // chartBaseOptions: config base de la gráfica
    // ----------------------------------------------------------------------
    chartBaseOptions () {
      const vm = this

      return {
        chart: {},
        xAxis: {
          type: 'datetime',
          minPadding: 0.03,
          maxPadding: 0.03,
          startOnTick: true,
          endOnTick: true,
          tickInterval: 7 * 24 * 60 * 60 * 1000
        },
        yAxis: [
          {
            title: { text: vm.$t('value') },
            labels: { style: { color: 'black' } },
            softMin: 0.5,
            softMax: 9.5,
            gridLineColor: GRAY_COLOUR,
            gridLineWidth: 0.25
          }
        ],
        credits: false,
        series: [
          {
            name: '',
            data: [],
            yAxis: 0
          }
        ]
      }
    },

    // ----------------------------------------------------------------------
    // finalChartOptions: Construye el objeto de configuración final
    // ----------------------------------------------------------------------
    finalChartOptions () {
      if (!this.chartData) return null

      // 1) Clonamos la configuración base
      const styledOptions = { ...this.chartBaseOptions }

      // =====================================================================
      // Construir la SERIE PRINCIPAL (areaspline / spline)
      // =====================================================================
      const variableArray = (this.chartData.variableData || [])
        .filter(item => item.date && !isNaN(new Date(item.date)))
        .map(item => {
          const xVal = new Date(item.date).getTime()
          const yOrig = this.minMaxTypeChart ? item.value.max : item.value
          // Valor recortado a [0.5, 9.5] para que nunca “toque” los bordes
          const colorPoint = this.coloringPoints
            ? this.getFeelingColor(item.value)
            : BLUE_COLOUR

          // Si la serie usa escala fija (maxValue existe) seguimos “clamp”
          const yClamped = this.autoScale
            ? yOrig
            : Math.max(0.5, Math.min(this.maxValue - 0.5, yOrig))

          return { x: xVal, y: yClamped, color: colorPoint, yOrig }
        })

      let variableMin = Infinity
      let variableMax = -Infinity
      variableArray.forEach(pt => {
        if (pt.x < variableMin) variableMin = pt.x
        if (pt.x > variableMax) variableMax = pt.x
      })
      const latest = variableMax
    const earliestVisible = Math.max(
      variableMin,
      latest - this.monthsWindow * MONTH_MS
    )

styledOptions.xAxis.min = earliestVisible
styledOptions.xAxis.max = latest

    const lineClr = ['area', 'areaspline'].includes(this.typeChart)
      ? BACKGROUND_COLOUR
      : BLUE_COLOUR
      const variableSeries = {
        name: this.label,
        data: variableArray,
        type: this.typeChart,
        zIndex: 0,
        lineWidth: 1.5,
        color: lineClr,
        visible: !this.hideMainSeriesByDefault,
        minDate: variableMin,
        maxDate: variableMax
      }
      // =====================================================================
      // Serie secundaria (min/max) - solo si es necesario
      // =====================================================================
      let minSeries = null
      if (this.minMaxTypeChart) {
        const minArray = (this.chartData.variableData || []).map(item => {
          const xVal = new Date(item.date).getTime()
          return { x: xVal, y: item.value.min }
        })

        let minSerMin = Infinity
        let minSerMax = -Infinity
        minArray.forEach(pt => {
          if (pt.x < minSerMin) minSerMin = pt.x
          if (pt.x > minSerMax) minSerMax = pt.x
        })

        minSeries = {
          name: this.$t('minimum'),
          data: minArray,
          type: 'spline',
          visible: true,
          marker: { enabled: true, radius: 4 },
          zIndex: 2,
          minDate: minSerMin,
          maxDate: minSerMax
        }
      }

      // =====================================================================
      // 4) MERGE de series (principal + min/max + adicionales)
      // =====================================================================
      const processedAdditional =
        this.additionalSeries.map(s => ({
          ...s,
          visible:
            this.hideAdditionalSeriesByDefault
              ? false
              : s.visible !== false
        }))
      const allSeries = [
        variableSeries,
        ...(minSeries ? [minSeries] : []),
        ...processedAdditional
      ].filter(s => s && s.data && s.data.length)
      // Ordenamos por fecha mínima para mantener coherencia visual
      allSeries.sort((a, b) => (a.minDate || 0) - (b.minDate || 0))
      styledOptions.series = allSeries
    // 5) Ajustar yAxis principal
    // – Variables “normales”: max 9.5
    // – Si viene maxValue (> 9.5) lo respetamos
      if (!this.autoScale) {
      // Escala fija → usa maxValue pasado por el padre
      styledOptions.yAxis[0].max = this.maxValue
      styledOptions.yAxis[0].softMax = this.maxValue
    } else {
      // Auto-escala → toma el dato máximo y añade 5 % de margen
      const rawMax = Math.max(...variableArray.map(p => p.yOrig))
      styledOptions.yAxis[0].max = rawMax * 1.05
      styledOptions.yAxis[0].softMax = rawMax * 1.05
    }
      // =====================================================================
      // 6) Generar ejes Y adicionales cuando haga falta
      // =====================================================================
      // const baseAxis = styledOptions.yAxis[0]
      // styledOptions.yAxis = [
      //   baseAxis, // eje principal (izquierda)
      //   ...this.yAxisAdditional // ejes adicionales definidos en la prop
      // ]
      // =====================================================================
      // 7) Tooltip personalizado
      // =====================================================================
      const vm = this
      styledOptions.tooltip = {
        useHTML: true,
        outside: false,
          formatter: function () {
    const dateStr = new Date(this.x).toLocaleDateString()
    const header = `<strong>${this.series.name}</strong>`
    if (vm.variableName === HEALTH_CONDITION_TODAY) {
      // sacamos la clave según el valor
      const key = getMoodEmojiKey(this.y)
      // y renderizamos la carita
      const emojiHtml = renderTickIconHtml(key, 20)
      return `
        ${emojiHtml}
      `
    }

    // en cualquier otro caso, el tooltip normal:
    const colorMarker = `<span style="color:${this.point.color}">●</span>`
    return `
      ${colorMarker} ${header}<br/>
      ${dateStr}: ${this.y}${vm.measureUnit}
    `
  }
      }
      return styledOptions
    }
  },

  // ----------------------------------------------------------------------
  // mounted: llamamos a la API para los datos de la variable
  // ----------------------------------------------------------------------
  mounted () {
    this.updateMonthsWindow()
    window.addEventListener('resize', this.updateMonthsWindow)
    ClinicHistoryRepository().getHistoryChart(this.variableName)
      .then(res => {
        const allData = res.data.data || []
        const sixBack = Date.now() - SIX_MONTHS_MS
        // datos para la serie (-6 meses)
        this.chartData.variableData = allData
        this.showChart = allData
          .filter(d => new Date(d.date) >= sixBack).length >= 2
          })
          .catch(err => {
            console.error('Error cargando chart data:', err)
          })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateMonthsWindow)
},
  methods: {
     updateMonthsWindow () {
    // 0-1023 px incluyen todos tus mixins de mobile + tablets
    const isMobileOrTablet = window.matchMedia('(max-width: 1023px)').matches
    this.monthsWindow = isMobileOrTablet ? 3 : 6
  },
    getFeelingColor (value) {
      if (value <= 1) {
        return RED_COLOUR
      } else if (value <= 3.5) {
        return ORANGE_MID_COLOUR
      } else if (value <= 6) {
        return YELLOW_COLOUR
      } else if (value <= 8.5) {
        return GREEN_LIGHT_COLOUR
      } else if (value <= 10) {
        return GREEN_COLOUR
      } else {
        return GREEN_COLOUR
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/ClinicHistoryVariableChart.vue